import { useFetchContext } from "../../common/FetchContext";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import { urls } from "../../common/urls";

export const useAssetTypes = () => {
  const { authAxios } = useFetchContext();

  const getAssetTypes = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.assetTypes.all);
    return data.results;
  };
  return useQuery("assetTypes", getAssetTypes);
};

export const useTestCommunicationDetails = (type: string) => {
  const { authAxios } = useFetchContext();
  let assetTypeFilter = "";
  if (type.includes("wet_leakage")) {
    assetTypeFilter = "WET LEAKAGE";
  } else if (type.includes("diode_test")) {
    assetTypeFilter = "DIODE TESTER";
  } else if (type.includes("visual_inspection")) {
    assetTypeFilter = "VISUAL INSPECTION";
  } else if (type.includes("colorimeter")) {
    assetTypeFilter = "COLORIMETER";
  } else if (type.includes("el")) {
    assetTypeFilter = "Electroluminescence Test Station";
  } else if (type.includes("iv_curve")) {
    assetTypeFilter = "Flash Tester";
  } else if (type.includes("insulation_test")) {
    assetTypeFilter = "Insulation Test";
  } else {
    assetTypeFilter = "";
  }

  const getTestCommunicationDetails = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.assetTypes.byName(assetTypeFilter),
    );
    return data.results[0];
  };
  return useQuery(["testCommunication", type], getTestCommunicationDetails);
};

export const useUnitsByAsset = (
  assetId: number,
  options: UseQueryOptions<any, any> = {},
) => {
  const { authAxios } = useFetchContext();

  const getUnitsByAsset = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.units.byAsset(assetId));

    return data;
  };

  return useQuery<any, any>({ queryFn: getUnitsByAsset, ...options });
};

export const useUnitsBySerialNumber = (serialNumber: string | null) => {
  const { authAxios } = useFetchContext();

  const getUnitsBySerialNumber = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.units.bySerialNumber(serialNumber),
    );
    return data.results;
  };
  return useQuery(
    ["unitsBySerialNumber", serialNumber],
    getUnitsBySerialNumber,
    {
      enabled:
        serialNumber !== undefined &&
        serialNumber !== null &&
        serialNumber !== "",
    },
  );
};

export const useIntakeBySerialNumber = (serialNumber: string) => {
  const { authAxios } = useFetchContext();

  const getIntakeImagesBySerialNumber = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.units.getIntakeImage(serialNumber),
    );
    return data;
  };
  return useQuery("intakeImage", getIntakeImagesBySerialNumber);
};

export const useAvailableDefects = () => {
  const { authAxios } = useFetchContext();

  const getAvailableDefects = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.availableDefects.all);
    return data.results;
  };
  return useQuery("available_defects", getAvailableDefects);
};

export const useProcedureResult = (id: string) => {
  const { authAxios } = useFetchContext();

  const getProcedureResult = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.procedureResults.procedureDetails(id),
    );
    return data;
  };
  return useQuery(["procedure_result", id], getProcedureResult, {
    enabled: id !== undefined && id !== null && id !== "",
  });
};

export const useProcedureResultView = (id: number) => {
  const { authAxios } = useFetchContext();

  const getProcedureResults = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.procedureResults.view(id));
    return data;
  };

  return useQuery(["procedureResultsView", id], getProcedureResults, {
    enabled: id !== undefined && id !== null,
  });
};
export const useUsers = () => {
  const { authAxios } = useFetchContext();

  const getUsers = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.users.all);
    return data.results;
  };
  return useQuery(["users"], getUsers);
};

//Available TSD
export const useAvailableTSD = () => {
  const { authAxios } = useFetchContext();

  const getAvailableTSD = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.testSequenceDefinitions.availableTsd,
    );
    return data;
  };
  return useQuery("available_tsd", getAvailableTSD);
};

// report for procedureName
export const useProcedureNameResult = (serialNumber: string) => {
  const { authAxios } = useFetchContext();

  const getSerialNumber = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportResult.getUnitsProcedure(serialNumber),
    );
    return data;
  };
  return useQuery("report_for_procedure_name", getSerialNumber);
};

export const useProcedureComments = (id: string | null, enabled = true) => {
  const { authAxios } = useFetchContext();

  const getProcedureComments = async (): Promise<any> => {
    if (!id) return [];

    const { data } = await authAxios.get(
      urls.ProcedureComments.getProcedureComments(id),
    );
    return data.results;
  };

  return useQuery(["procedureComments", id], getProcedureComments, {
    enabled: enabled && !!id,
  });
};

export const useSerialNumber = (work_order_id: string) => {
  const { authAxios } = useFetchContext();

  const getSerialNumber = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportResult.getUnits(work_order_id),
    );
    return data;
  };

  return useQuery("report_for_serial_number", getSerialNumber);
};

export const SubmitComment = async ({
  authAxios,
  procedure_result,
  comments,
  subject,
  tagged_users,
  azurefiles,
}: any): Promise<any> => {
  const { data } = await authAxios.post(urls.ProcedureComments.submit, {
    procedure_result,
    comments,
    subject,
    tagged_users,
    ...(azurefiles?.length > 0 && {
      azurefiles,
    }),
  });

  return data;
};

export const useSubmitComment = () => {
  return useMutation(SubmitComment);
};

export const useAcknowledgeProcedureComments = () => {
  const { authAxios } = useFetchContext();
  const queryClient = useQueryClient();

  return useMutation(
    (procedureResultId: number) =>
      authAxios.post(
        urls.ProcedureComments.recordAcknowledgement, // wire this to /procedureresult_comments/record_aknowledgement/
        { procedure_result_id: procedureResultId },
      ),
    {
      onSuccess: (_data, procedureResultId) => {
        queryClient.invalidateQueries(["procedureComments", procedureResultId]);
      },
    },
  );
};
